import moment from "moment";

/**
 * Plain-language wording for the policy's History dialog (components/JobHistoryDialog.js): job statuses, timeline
 * entries and wallet rows as sentences an operator reads at a glance, instead of the queue's own words.
 */

const inr = (amount) =>
  `₹${(Math.round((Number(amount) || 0) * 100) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "17 Oct 2026, 05:20:59 PM" — with seconds: several steps often happen in the same minute. */
export const fmtTime = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm:ss A") : "—");

const STATUS_WORDS = {
  pending: "Waiting for the automation",
  processing: "Automation running",
  retrying: "Will try again",
  hold: "On hold",
  completed: "Completed",
  complete: "Completed",
  completed_with_errors: "Completed with errors",
  failed: "Failed",
  failed_post_submission: "Failed after submitting",
  failed_login_form: "Failed at the insurer's form",
  failed_validation: "Failed — data not accepted",
  stopped: "Stopped on purpose",
  cancelled: "Cancelled",
  skipped: "Skipped",
};

/** A job status in words ("pending" -> "Waiting for the automation"). */
export const statusWords = (status) => {
  const s = String(status || "").toLowerCase();
  if (!s) return "—";
  if (STATUS_WORDS[s]) return STATUS_WORDS[s];
  if (s.startsWith("failed")) return "Failed";
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
};

/** One timeline entry ({from, to, note}) as a sentence. */
export const timelineSentence = (entry = {}) => {
  const note = String(entry.note || "");
  const to = String(entry.to || "").toLowerCase();
  const from = String(entry.from || "").toLowerCase();
  if (to === "hold" && /until the wallet has money/i.test(note)) return "Saved on hold until the wallet has money — nothing charged";
  if (from === "hold" && to === "pending" && /released from hold/i.test(note)) return "Hold removed — sent to the automation";
  if (to === "hold" && /held by operator/i.test(note)) return "Put on hold — the automation will not start it";
  if (/edited/i.test(note)) return `Policy edited — ${statusWords(to).toLowerCase()}${/still on hold/i.test(note) ? " (still on hold)" : ""}`;
  if (!from) return `Created — ${statusWords(to).toLowerCase()}`;
  const clean = note
    .replace(/^job (created|updated|re-created) (on|after) /i, "")
    .replace(/^policy /i, "")
    .trim();
  return `${statusWords(from)} → ${statusWords(to)}${clean ? ` · ${clean}` : ""}`;
};

/** One wallet row of the policy as a sentence, and whether money went out (debit) or came back. */
export const moneySentence = (move = {}) => {
  const out = move.type === "POLICY_DEBIT";
  const wallet =
    move.wallet === "POLICY"
      ? `${move.ownerName ? `${move.ownerName}'s` : "the user's"} Policy Wallet`
      : `${move.company || "the"} Company Wallet`;
  return {
    out,
    text: out ? `Charged ${inr(move.amount)} from ${wallet}` : `Given back ${inr(move.amount)} to ${wallet}`,
  };
};

/**
 * The policy's payment in one chip: from its payment record when it has one, else from its wallet rows (older policies).
 * @returns {{ label, tone: "success"|"error"|"warning"|"default", detail? }}
 */
export const paymentSummary = (data = {}) => {
  const pay = data.payment;
  if (pay && pay.state === "WAITING_FOR_MONEY") {
    const companies = (pay.required && pay.required.companies) || [];
    const parts = companies.map((c) => `${c.company} Company Wallet ${inr(c.amount)}`);
    if (pay.required && Number(pay.required.policyWallet) > 0) parts.push(`the creator's Policy Wallet ${inr(pay.required.policyWallet)}`);
    return { label: "Not paid — waiting for wallet money", tone: "error", detail: parts.length ? `Remove hold will charge ${parts.join(" + ")}` : "" };
  }
  if (pay && pay.state === "CHARGING") return { label: "Charging the wallet now", tone: "warning" };
  if (pay && pay.state === "NOT_NEEDED") return { label: "Payment link — the customer pays the insurer", tone: "default" };
  const net = (data.walletMoves || []).reduce(
    (sum, m) => sum + (m.wallet === "COMPANY" ? (m.type === "POLICY_DEBIT" ? 1 : -1) * (Number(m.amount) || 0) : 0),
    0
  );
  if ((pay && pay.state === "PAID") || net > 0.004) return { label: `Paid ${inr(Math.max(net, 0))}`, tone: "success" };
  if (String(data.paymentMethod || "").toLowerCase() === "link") return { label: "Payment link — the customer pays the insurer", tone: "default" };
  return { label: "Nothing charged", tone: "default" };
};
